// Cálculos del módulo Mi Negocio (stock y ventas).

const num = (n) => Number(n || 0);

// Lo que de verdad te queda de la venta: el total cobrado menos el IVA que hay que declarar
// (las ventas sin documento tienen iva = 0, así que a esas no les cambia nada).
export function ventaNeta(v) {
  return totalVenta(v) - num(v.iva);
}

// Ganancia real de una venta: la venta sin IVA, menos lo que costó la mercadería y los gastos
// extra. El IVA que cobras no es tuyo, por eso no cuenta como ganancia.
export function gananciaVenta(v) {
  return ventaNeta(v) - num(v.cantidad) * num(v.costo_unitario) - num(v.costo_extra);
}

export function totalVenta(v) {
  return num(v.cantidad) * num(v.precio_unitario);
}

// Una venta devuelta se conserva como registro, pero no vendió nada.
export const ventaCuenta = (v) => !v.devuelta;

// Las ventas por Mercado Libre se pueden registrar sin monto (la plata se lleva por el SII):
// esas solo mueven stock, así que no entran en los totales de dinero. Si entraran, el costo
// de la mercadería sin ingreso que lo compense aparecería como pérdida.
export const ventaConMonto = (v) => !v.devuelta && num(v.precio_unitario) > 0;

// Stock actual de cada producto = entradas/ajustes - unidades vendidas.
export function stockPorProducto(movimientosStock, ventas) {
  const r = {};
  movimientosStock.forEach(m => { r[m.producto_id] = (r[m.producto_id] || 0) + num(m.cantidad); });
  // Una venta devuelta no sacó nada: sus unidades siguen en el stock
  ventas.filter(ventaCuenta).forEach(v => { r[v.producto_id] = (r[v.producto_id] || 0) - num(v.cantidad); });
  return r;
}

// Costo promedio ponderado al agregar una entrada de mercadería.
export function costoPromedio(stockActual, costoActual, cantidadNueva, costoNuevo) {
  const stock = Math.max(0, num(stockActual));
  const total = stock + num(cantidadNueva);
  if (total <= 0) return num(costoNuevo);
  return Math.round((stock * num(costoActual) + num(cantidadNueva) * num(costoNuevo)) / total);
}

// Qué te cobró cada proveedor por un producto, del más barato al más caro.
// Compara el costo que queda guardado: en las compras con factura ese costo ya es el neto,
// que es lo que de verdad te cuesta el producto.
export function preciosPorProveedor(movimientosStock, productoId) {
  const porProveedor = {};
  movimientosStock
    .filter(m => m.producto_id === productoId && m.tipo === 'entrada' && num(m.costo_unitario) > 0)
    .forEach(m => {
      const nombre = (m.proveedor || '').trim() || 'Sin proveedor';
      const p = porProveedor[nombre] || (porProveedor[nombre] = {
        proveedor: nombre, veces: 0, unidades: 0, costoTotal: 0, ultimaFecha: '', ultimoCosto: 0, conFactura: false,
      });
      p.veces += 1;
      p.unidades += num(m.cantidad);
      p.costoTotal += num(m.cantidad) * num(m.costo_unitario);
      if (!p.ultimaFecha || (m.fecha || '') >= p.ultimaFecha) {
        p.ultimaFecha = m.fecha || '';
        p.ultimoCosto = num(m.costo_unitario);
        p.conFactura = !!m.con_factura;
      }
    });
  return Object.values(porProveedor)
    .map(p => ({ ...p, costoPromedio: p.unidades > 0 ? Math.round(p.costoTotal / p.unidades) : 0 }))
    .sort((a, b) => a.ultimoCosto - b.ultimoCosto);
}

// El proveedor que te dejó el último precio más barato.
export function mejorProveedor(movimientosStock, productoId) {
  return preciosPorProveedor(movimientosStock, productoId)[0] || null;
}

// Métricas por producto a partir de sus ventas.
export function metricasProducto(ventasProducto) {
  const vendidas = ventasProducto.filter(ventaCuenta);
  const conMonto = ventasProducto.filter(ventaConMonto);
  const unidades = vendidas.reduce((s, v) => s + num(v.cantidad), 0);
  const unidadesConMonto = conMonto.reduce((s, v) => s + num(v.cantidad), 0);
  const ingresos = conMonto.reduce((s, v) => s + totalVenta(v), 0);
  const ganancia = conMonto.reduce((s, v) => s + gananciaVenta(v), 0);
  return {
    unidades,
    ingresos,
    ganancia,
    gananciaPorUnidad: unidadesConMonto > 0 ? ganancia / unidadesConMonto : 0,
    margen: ingresos > 0 ? (ganancia / ingresos) * 100 : 0,
  };
}
